<!--
  Component: VizCompBars

  This component creates an svg visualization for a bar graph with multiple
  groups of bars, showing a bar for the main value as compared to a comparison
  bar ('comp', may or may not be comp group) value.

  Notes:
  - Number of bar groups is determined automatically based on length of barData
  - Number of lines in rowLabel for each bar group is determined automatically
  - Overall height will automatically calculated based on above info

  Props:
  - barData - array of data to be used to display in bar graphs, one item for
      each bar group "row".  Each item should be an object with the following
      properties:
    - rowLabel - string, or array of text (for multiple lines) to be shown to
        left of bars
    - nVal - (optional) n to be shown in parenthesis at end of last line of row
        labels, if available
    - mainVal - numeric value between 0 and maxRange, used for scaling main bar
    - compVal - numeric value or array of values, between 0 and maxRange,
        used for scaling comp bar(s)
    - mainN - (optional) n to be shown in parenthesis after main bar val label
    - compN - (optional) n or array of ns to be shown in parenthesis after
        val label for comp bar(s)
    - valLabel - (optional) text to be shown to right of bars, overrides
        default behavior of calculating based on mainVal and valFormat
  - vizWidth - (optional) for total area of graph
  - isFullWidth - (optional) flag overriding the use of VizWidth, the width
      becomes 100% and responsive for the graph
  - rowLabelWidth - space on left side for labels; 0 axis starts after that
  - valLabelWidth - space on right of bars for labels
  - paddingX - horizontal spacing between labels and bars, except valLabel
  - mBarHeight, cBarHeight - (optional) main and comp bar heights
  - maxBarWidth - (optional) width of bar at max range value, value label is to
      the right of this, if not supplied will be automatically calculated based
      on vizWidth and rowLabelWidth (and valLabelWidth, if showValLabels)
  - rangeMax - (optional) padding in pixels between/around bars
  - valFormat - (optional) format to be applied to valLabel text
  - showValLabels - (optional) flag to show/calculate val labels
  - equalBarHeights - (optional) enabling this overrides comp bar height to
      to match main bar height.  Also allows behavior that was disabled by
      having small comp bars (comp bar labels, values right after bar instead
      of after longest bar)
  - legend (optional) - in format used by LegendObjectMixin
  - mainCssClass - (optional) css class to add to main bar / legend items
  - compCssClasses - (optional) array of css class strings to add to comp bars
      and legend items.
  - ariaLabelText - (optional) uses label as text for whole component if present,
      otherwise defaults to regular behavior of reading all contents
  - immediateMainLabel - (optional) main bar label not after the longest bar
-->

<template>
  <!--
  Use aria-label because title changes aren't capture automatically by NVDA
  Not using aria-labelledby because then we would need unique id passed in from outside

  Another alternative is to use aria-hidden='true', with sr-only span:
  https://css-tricks.com/gotchas-getting-inline-svg-production-part-ii/#article-header-id-9
   -->
  <div class="viz-comp-bars" ref="svgDiv">
    <svg class="viz-comp-bars-svg"
      focusable="false"
      :role="ariaLabelText!==undefined && ariaLabelText !== null ? 'img' : undefined"
      :aria-label="ariaLabelText"
      :width="this.isFullWidth ? '100%' : vizWidth"
      :height="vizHeight"
      >
      <g
        v-if="hasLegend"
        class="legend"
        :transform="`translate(${legendX},0)`"
      >
        <template v-for="(dataGroup, i) in [legendObject.main, legendObject.comp]">
          <g v-for="(typeGroup, j) in dataGroup"
            :key="`legend-item-${i}-${j}`"
            class="legend-item"
            :transform="`translate(0,${legendPaddingTop+maxLinesPerLegendItem*((i > 0 ? numMainLegendGroupItems : 0) + j)*legendLineSpacing})`"
          >
            <rect
              :class="['bar', i === 0 ? 'main' : 'comp', i === 0 ? mainCssClass : compCssClasses[j]]"
              :width="legendBarWidth"
              :height="i === 0 ? mBarHeight : _cBarHeight"
              :y="-Math.floor(0.5*(i === 0 ? mBarHeight : _cBarHeight))"
            />
            <text v-for="(text, k) in typeGroup"
              :key="`legend-text-${i}-${j}-${k}`"
              :x="legendBarWidth+paddingX"
              :y="k*legendLineSpacing"
              dy="0.4em"
            >
              {{text}}
            </text>
          </g>
        </template>
      </g>
      <g class="plot-area" :transform="`translate(${rowLabelWidth+paddingX},${legendHeight})`">
        <g class="bar-groups">
          <g v-for="(d,i) in barData"
            :key="`row-${i}`"
            class="bar-group"
            :transform="`translate(0,${barGroupY(i)})`">
            <!-- Vertically align center of labels on left with center of bars on right -->
            <g class="bar-group-labels"
              :transform="`translate(${-paddingX},${0.5*barGroupHeights[i]})`"
            >
              <text v-for="j in numLabelLinesArray[i]"
                :key="`row-${i}-label-${j-1}`"
                text-anchor="end"
                dy="0.25em"
                :y="(j-1-0.5*(numLabelLinesArray[i]-1))*labelLineSpacing"
                >{{labelForRowAndLine(i,j-1)}}</text>
            </g>
            <g class="bar-group-items"
              :transform="`translate(${barX},${Math.floor(0.5*(barGroupHeights[i]-rowBarsHeight))})`"
            >
              <!--
              Note: text is top-aligned with bar, would need to add offsets to
              center-align them for varying-size bars, but this may overlap for
              small bars anyhow.
              -->
              <g class="bar-group-item">
                <rect :class="['bar main', mainCssClass]"
                  :height="mBarHeight"
                  :width="xScale(mainValForRow(i))"
                />
                <text
                  :dy="textDY"
                  :x="xScale(immediateMainLabel || comparisonHidden || equalBarHeights ? mainValForRow(i) : largestValForRow(i))+valLabelPaddingX"
                  >{{mainValLabelForRow(i)}}</text>
              </g>
              <g v-for="(cVal, jc) in compValsForRow(i)"
                :key="`row-${i}-comp-${jc-1}`"
                class="bar-group-item"
                :transform="`translate(0,${mBarHeight+(jc+1)*barPaddingY+jc*_cBarHeight})`"
              >
                <rect
                  :class="['bar comp', compCssClasses[jc]]"
                  :height="_cBarHeight"
                  :width="xScale(cVal)"
                />
                <text
                  class="comp"
                  :dy="textDY"
                  :x="xScale(equalBarHeights ? cVal : largestValForRow(i))+valLabelPaddingX"
                  v-html="compValLabelForRowAndIndex(i, jc)"></text>
              </g>
            </g>
          </g>
        </g>
        <line class="axis" :y2="plotHeight" :stroke-width="axisWidth"/>
      </g>
    </svg>
  </div>
</template>

<script>
import Component, { mixins } from 'vue-class-component'
import { LegendObjectMixin } from '@/mixin'

import * as d3 from 'd3-scale'
import isNumber from 'lodash/isNumber'
import isString from 'lodash/isString'
import isArray from 'lodash/isArray'

import { isNonNegative, valIsMissing, valIsNA, valIsNLT, valIsPara, formatValue } from '@/utils/data'

// Defaults for sizing
const DEFAULT_VIZ_WIDTH = 300 // For 320px mobile min width, with 10px paddings
const DEFAULT_ROW_LABEL_WIDTH = 100
const DEFAULT_VAL_LABEL_WIDTH = 40
const DEFAULT_MAIN_BAR_HEIGHT = 13
const DEFAULT_COMP_BAR_HEIGHT = 6

const LEGEND_X = 20
const LEGEND_BAR_WIDTH = 28
const LEGEND_PADDING_TOP = 11
const LEGEND_PADDING_BOTTOM = 5
const LEGEND_LINE_SPACING = 16
const AXIS_WIDTH = 1

const TEXT_DY = '0.7em'

// Paddings
const DEFAULT_PADDING_X = 6
const VAL_LABEL_PADDING_X = 2
export const PLOT_PADDING_Y = 6
const BAR_PADDING_Y = 3
const LABEL_LINE_SPACING = 16
const ROW_PADDING_BOTTOM = 10

// Default for percents on 0-1 scale
const DEFAULT_RANGE_MIN = 0
const DEFAULT_RANGE_MAX = 1
const DEFAULT_VAL_FORMAT = 'p1.00'

@Component({
  props: {
    ariaLabelText: String,
    barData: Array,
    isFullWidth: {
      type: Boolean,
      default: false
    },
    vizWidth: {
      type: Number,
      validator: isNonNegative,
      default: DEFAULT_VIZ_WIDTH
    },
    rowLabelWidth: {
      type: Number,
      validator: isNonNegative,
      default: DEFAULT_ROW_LABEL_WIDTH
    },
    valLabelWidth: {
      type: Number,
      validator: isNonNegative,
      default: DEFAULT_VAL_LABEL_WIDTH
    },
    paddingX: {
      type: Number,
      validator: isNonNegative,
      default: DEFAULT_PADDING_X
    },
    mBarHeight: {
      type: Number,
      validator: isNonNegative,
      default: DEFAULT_MAIN_BAR_HEIGHT
    },
    cBarHeight: {
      type: Number,
      validator: isNonNegative,
      default: DEFAULT_COMP_BAR_HEIGHT
    },
    maxBarWidth: {
      type: Number,
      validator: isNonNegative
      // No default - autocalculated if not provided
    },
    rangeMax: {
      type: Number,
      default: DEFAULT_RANGE_MAX
    },
    valFormat: {
      type: String,
      default: DEFAULT_VAL_FORMAT
    },
    showValLabels: {
      type: Boolean,
      default: true
    },
    equalBarHeights: {
      type: Boolean,
      default: false
    },
    labelGreaterAndLessThanPercent: {
      type: Boolean,
      default: false
    },
    mainCssClass: {
      type: String,
      default: ''
    },
    compCssClasses: {
      type: Array,
      default: () => []
    },
    rowPaddingBottom: {
      type: Number,
      default: ROW_PADDING_BOTTOM
    },
    plotPaddingY: {
      type: Number,
      default: PLOT_PADDING_Y
    },
    comparisonHidden: {
      type: Boolean,
      default: false
    },
    immediateMainLabel: {
      type: Boolean,
      default: false
    }
  }
})
export default class VizCompBars extends mixins(LegendObjectMixin) {
  legendX = LEGEND_X
  legendBarWidth = LEGEND_BAR_WIDTH
  legendPaddingTop = LEGEND_PADDING_TOP
  legendLineSpacing = LEGEND_LINE_SPACING

  textDY = TEXT_DY

  axisWidth = AXIS_WIDTH
  rangeMin = DEFAULT_RANGE_MIN

  valLabelPaddingX = VAL_LABEL_PADDING_X
  barPaddingY = BAR_PADDING_Y
  labelLineSpacing = LABEL_LINE_SPACING

  vizObservedWidth = this.vizWidth

  observeVizWidth () {
    const targetNode = this.$refs.svgDiv
    this.vizObservedWidth = targetNode.clientWidth

    const observer = new ResizeObserver(entries => {
      const w = entries[0].target.clientWidth
      // w is 0 when the component is hidden, When it's hidden, the value doesn't matter.
      // we need the non-zero value for printing because print preview would not wait for the effect
      // of unhiding when Ctrl-P or Print button is pressed.
      if (w) {
        this.vizObservedWidth = w
      }
    })

    observer.observe(targetNode)
  }

  mounted () {
    if (this.isFullWidth) {
      this.observeVizWidth()
    }
  }

  /* Legend - code below specific to compatibility of previous VizCompBars legend; now moved into VizLegendDivs */

  get numMainLegendGroupItems () {
    return this.legendObject.main.length
  }

  get numLegendLinesSpacing () {
    return this.numLegendItems * this.maxLinesPerLegendItem
  }

  get numLegendItems () {
    return this.flattenedLegendItems.length
  }

  get maxLinesPerLegendItem () {
    return Math.max(...this.flattenedLegendItems.map(d => d.length))
  }

  /* Sizing and positioning */

  get numRows () {
    return this.barData.length
  }

  get legendHeight () {
    return this.hasLegend
      ? this.numLegendLinesSpacing * this.legendLineSpacing + LEGEND_PADDING_TOP + LEGEND_PADDING_BOTTOM : 0
  }
  get plotHeight () {
    if (this.numRows === 0) return 0
    return this.barGroupYArray[this.numRows] - this._rowPaddingBottom + this.plotPaddingY
  }
  get vizHeight () {
    return this.legendHeight + this.plotHeight
  }
  get rowBarsHeight () {
    return this.mBarHeight + (this.barPaddingY + this._cBarHeight) * this.numCompBars
  }
  get barX () {
    return 0.5 * this.axisWidth
  }

  // Internal version of comp bar height
  get _cBarHeight () {
    return this.equalBarHeights ? this.mBarHeight : this.cBarHeight
  }

  // Since null is an allowed value by prop validators, assume user wants
  //  default value if they passed in null instead of 0
  // Internal version of width for value labels
  get _valLabelWidth () {
    const w = this.valLabelWidth
    return w === null ? DEFAULT_VAL_LABEL_WIDTH : w
  }
  // Internal version of padding between bars to avoid using nulls as 0
  get _rowPaddingBottom () {
    const w = this.rowPaddingBottom
    return w === null ? ROW_PADDING_BOTTOM : w
  }

  // Internal version of max width used for calculating bar lengths for display
  get _maxBarWidth () {
    if (this.maxBarWidth) return this.maxBarWidth
    let w = (this.isFullWidth ? this.vizObservedWidth : this.vizWidth) -
      this.rowLabelWidth - this.paddingX - 0.5 * this.axisWidth
    if (this.showValLabels) {
      w = w - this.valLabelPaddingX - this._valLabelWidth
    }

    return Math.max(w, 0)
  }

  // Pre-calculate and store scaling function for bars
  get xScale () {
    return d3.scaleLinear()
      .clamp(true)
      .domain([this.rangeMin, this.rangeMax])
      .range([0, this._maxBarWidth])
  }

  get barGroupHeights () {
    const result = []
    let numLines, lineSpacing
    for (let i = 0; i < this.numRows; i++) {
      numLines = this.numLabelLinesArray[i]
      // single lines may be smaller than the bar heights
      lineSpacing = Math.max(this.labelLineSpacing * numLines, this.rowBarsHeight)
      result.push(lineSpacing)
    }
    return result
  }

  // Pre-calculate/cache y locations of each bar group, plus an extra one
  //  so we know where bottom of graph is
  get barGroupYArray () {
    const yArray = [this.plotPaddingY]
    for (let i = 0; i < this.numRows; i++) {
      yArray.push(yArray[i] + this.barGroupHeights[i] + this._rowPaddingBottom)
    }

    return yArray
  }

  /* Methods */

  isInvalidRow (row) {
    if (row >= this.numRows || row < 0) {
      console.log('Warning: Exceeded bounds for bar data')
      return true
    }
    return false
  }

  barGroupY (row) {
    return this.isInvalidRow(row) ? 0 : this.barGroupYArray[row]
  }

  /* Processing Bar Data */

  rawValForRowAndKey (row, key) {
    if (this.isInvalidRow(row)) return undefined
    return this.barData[row][key]
  }

  valForRowAndKey (row, key) {
    const rv = this.rawValForRowAndKey(row, key)
    return this.numValForRawVal(rv)
  }

  // Defaults to 0 length bar for missing and NA
  numValForRawVal (rv) {
    let result = this.rangeMin
    if (!valIsMissing(rv) && !valIsNA(rv) && !isNaN(rv)) {
      result = +rv
    }
    return result
  }

  // Allows overriding with valLabel if one is provided
  mainValLabelForRow (row) {
    const s = this.rawValForRowAndKey(row, 'valLabel')
    if (s !== undefined) {
      return s
    }

    const v = this.rawValForRowAndKey(row, 'mainVal')
    const n = this.mainNForRow(row)
    return this.barLabelForValueAndN(v, n)
  }

  // Allows missing and NA values for display purposes
  barLabelForValueAndN (v, n) {
    if (!this.showValLabels) return ''
    if (!valIsMissing(v) && !valIsNA(v) && !valIsNLT(v) && !valIsPara(v) && isNaN(v)) {
      console.log(`Warning: VizCompBars: Input val data is invalid: ${v}`)
      return v
    }

    const s = []
    if (valIsMissing(v)) {
      // Leave blank, allow showing just n
    } else if (valIsNA(v) || valIsNLT(v) || valIsPara(v)) {
      s.push(v)
    } else if (this.labelGreaterAndLessThanPercent && +v !== 0 && +v < 0.01) {
      s.push('<1%')
    } else if (this.labelGreaterAndLessThanPercent && +v > 0.99 && +v < 1.00) {
      s.push('>99%')
    } else {
      s.push(formatValue(+v, this.valFormat))
    }

    if (!valIsMissing(n)) {
      s.push(`(${n})`)
    }
    return s.join(' ')
  }

  mainValForRow (row) {
    return this.valForRowAndKey(row, 'mainVal')
  }

  compValLabelForRowAndIndex (row, idx) {
    // For now, assume there is only space for comp bar labels if bars are
    //  equal heights (even though user could pass in larger cBarHeight)
    if (!this.equalBarHeights) return ''
    const v = this.compRawValArray[row][idx]
    const n = this.compNArray[row][idx]
    return this.barLabelForValueAndN(v, n)
  }

  // Pre-calculate/cache 2D arrays for comp to avoid re-calculating multiple
  //  times when accessing
  get compValArray () {
    // Numeric values used for displaying bars
    return this.compRawValArray.map(row => {
      return row.map(this.numValForRawVal)
    })
  }
  get compRawValArray () {
    // Raw values used for labels
    return this.barDataCompKeyToMatrix('compVal')
  }
  get compNArray () {
    return this.barDataCompKeyToMatrix('compN')
  }

  barDataCompKeyToMatrix (key) {
    let item, row
    const result = []
    for (let i = 0; i < this.numRows; i++) {
      item = this.barData[i][key]
      row = this.compItemToArray(item)
      result.push(row)
    }
    return result
  }

  compItemToArray (c) {
    let result = []
    if (c === undefined || c === null) {
      // Use default empty array if parent did not supply this parameter
    } else if (isString(c) || isNumber(c)) {
      result = [c]
    } else if (isArray(c)) {
      result = c
    } else {
      console.log('Warning: Unknown comp type: ', c)
    }
    return result
  }

  // Allow missing comp values - use group with longest set of bars
  //  Also does not check indexes, e.g. if they skip differnt comp bars
  get numCompBars () {
    return Math.max(...this.compValArray.map(d => d.length))
  }

  compValsForRow (row) {
    return this.isInvalidRow(row) ? undefined : this.compValArray[row]
  }

  largestValForRow (row) {
    return Math.max(this.mainValForRow(row), ...this.compValsForRow(row))
  }

  nValForRow (row) {
    return this.isInvalidRow(row) ? undefined : this.barData[row].nVal
  }

  mainNForRow (row) {
    return this.isInvalidRow(row) ? undefined : this.barData[row].mainN
  }

  compNsForRow (row) {
    return this.isInvalidRow(row) ? undefined : this.compNArray[row]
  }

  labelForRow (row) {
    return this.isInvalidRow(row) ? undefined : this.barData[row].rowLabel
  }

  get numLabelLinesArray () {
    return Array.from({ length: this.numRows }).map((d, i) => this.numLabelLinesForRow(i))
  }

  numLabelLinesForRow (row) {
    const rl = this.labelForRow(row)
    if (rl === undefined) {
      return 0
    } else if (isString(rl)) {
      return 1
    } else if (isArray(rl)) {
      return rl.length
    }
    console.log(`Warning: Invalid label line data: ${rl}`)
    return 0
  }

  labelForRowAndLine (row, line) {
    const numLines = this.numLabelLinesArray[row]
    if (line >= numLines || line < 0) {
      console.log('Warning: Exceeded bounds for bar line data')
      return undefined
    }

    const rl = this.labelForRow(row)
    let t = ''
    if (isString(rl)) {
      t = rl
    } else if (isArray(rl)) {
      t = rl[line]
    }

    // Add n to end of last line, if available
    if (line === numLines - 1) {
      const n = this.nValForRow(row)
      if (!valIsMissing(n)) {
        t += ` (${n})`
      }
    }

    return t
  }
}
</script>

<!-- These are base-version formatting rules meant to be overridden, so keep them unscoped -->
<style lang="scss">
.viz-comp-bars {
  .bar.comp {
    fill: #949494;
  }
  .bar.main {
    fill: #555555;
  }
  .legend text {
    fill: #555555;
  }
  line.axis {
    stroke: black;
  }
}
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
</style>



// WEBPACK FOOTER //
// src/components/VizCompBars.vue